import React, { useState } from 'react';
import { generatePath, RouteComponentProps, withRouter } from 'react-router';
import { Container, Fade } from 'reactstrap';
import {
  completeCarePlanActivity,
  updateCarePlan,
} from '../../../apis/carePlan';
import { updateConditionsByPatientId } from '../../../apis/conditions';
import { getQuestionnaires } from '../../../apis/questionnaire';
import { DEFAULT_QUESTIONNAIRES } from '../../../constants';
import { PatientFormCard } from '../../../containers/Cards';
import CarePlanProvider from '../../../containers/Providers/CarePlan';
import OrganizationProvider from '../../../containers/Providers/Organizations';
import PatientProvider from '../../../containers/Providers/Patient';
import CristalQuestionnaire from '../../../containers/Questionnaires/Cristal';
import { CarePlanContext } from '../../../contexts/carePlan';
import { usePractitioner } from '../../../contexts/practitioner';
import { CristalConditionsGenerator } from '../../../modules/unsw-fhir-conditions';
import { ICarePlan } from '../../../modules/unsw-fhir/CarePlan';
import { codeableConceptEqualHasCode } from '../../../modules/unsw-fhir/CodeableConcept';
import { IPractitioner } from '../../../modules/unsw-fhir/Practitioner';
import { IQuestionnaire } from '../../../modules/unsw-fhir/Questionnaire';
import { IQuestionnaireResponse } from '../../../modules/unsw-fhir/QuestionnaireResponse';
import { Wizard, WizardBanner, WizardPage } from '../../../modules/unsw-wizard';
import { PageContext } from '../../../modules/unsw-wizard/Contexts/PageContext';

const processConditions = async (
  questionnaire: IQuestionnaire,
  response: IQuestionnaireResponse,
  patientId: string,
  practitioner: IPractitioner,
) => {
  const conditions = new CristalConditionsGenerator(
    questionnaire,
  ).generateConditions(
    response,
    {
      resourceType: 'Patient',
      id: patientId,
    },
    practitioner,
  );
  const savedConditions = await updateConditionsByPatientId(
    conditions,
    patientId,
  );
  return savedConditions;
};

async function proccessCristal(
  carePlan: ICarePlan,
  questionnaire: IQuestionnaire,
  response: IQuestionnaireResponse,
  patientId: string | undefined,
  practitioner: IPractitioner,
) {
  const conditions = await processConditions(
    questionnaire,
    response,
    patientId!,
    practitioner,
  );
  const questionnaires = await getQuestionnaires(true);
  const requiredQuestionaires = questionnaires.filter(
    q =>
      conditions.some(
        condition =>
          condition.clinicalStatus !== 'inactive' &&
          codeableConceptEqualHasCode(condition.code, q.name),
      ) || DEFAULT_QUESTIONNAIRES.some(dQ => dQ === q.name),
  );
  return await updateCarePlan(carePlan, requiredQuestionaires, conditions);
}

const PatientNew = (props: RouteComponentProps<{ organizationId: string }>) => {
  const organizationId = props.match.params.organizationId;
  const [patientId, setPatientId] = useState<string | undefined>(undefined);
  const { practitioner } = usePractitioner();
  if (!practitioner || !practitioner.id) return null;
  const path =
    patientId && organizationId
      ? generatePath(`/Organization/:organizationId/patient/:patientId`, {
          patientId,
          organizationId,
        })
      : undefined;
  return (
    <OrganizationProvider organizationId={organizationId}>
      <PatientProvider patientId={patientId}>
        <CarePlanProvider patientId={patientId}>
          <CarePlanContext.Consumer>
            {({ setCarePlan, carePlan }) => (
              <Wizard onComplete={() => path && props.history.push(path)}>
                <WizardBanner />
                <Fade>
                  <WizardPage
                    name="1"
                    title="Patient Details"
                    subTitle="Patient Registration Forms">
                    <Container>
                      <PageContext.Consumer>
                        {({ onComplete }) => (
                          <PatientFormCard
                            onComplete={id => {
                              setPatientId(id);
                              onComplete();
                            }}
                            patientId={patientId}
                          />
                        )}
                      </PageContext.Consumer>
                    </Container>
                  </WizardPage>
                </Fade>
                <Fade>
                  <CristalQuestionnaire
                    pageId="2"
                    disabled
                    practitionerId={practitioner.id!}
                    onComplete={async (questionnaire, response) => {
                      if (!response) return;
                      let lCarePlan =
                        questionnaire.name === 'cristal' && response
                          ? await proccessCristal(
                              carePlan!,
                              questionnaire,
                              response,
                              patientId,
                              practitioner,
                            )
                          : carePlan;
                      if (lCarePlan) {
                        lCarePlan = await completeCarePlanActivity(
                          lCarePlan,
                          questionnaire.id!,
                          response.id!,
                        );
                      }
                      await setCarePlan(lCarePlan);
                      props.history.push(
                        `/Organization/${organizationId}/Patient/${patientId}/`,
                      );
                    }}
                  />
                </Fade>
              </Wizard>
            )}
          </CarePlanContext.Consumer>
        </CarePlanProvider>
      </PatientProvider>
    </OrganizationProvider>
  );
};
export default withRouter(PatientNew);
